import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Shield, Check, Upload, AlertTriangle, Send, CheckCircle, X, Inbox, Home, ShoppingCart, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// WhatsApp Icon
const WhatsAppIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

const SCAM_TYPES = [
  'Investment Scam',
  'Crypto Fraud',
  'Romance Scam',
  'Job Scam',
  'Phishing Email',
  'WhatsApp Impersonation',
  'Fake Website',
  'Parcel Delivery Scam',
  'Tech Support Scam',
  'Other'
];

// File limits by tier - Standardized naming
const TIER_FILE_LIMITS = {
  basic: { maxFiles: 3, label: 'Instant Scam Check', maxSizePerFile: 10 * 1024 * 1024 },
  pro: { maxFiles: 5, label: 'Advanced Risk Analysis', maxSizePerFile: 10 * 1024 * 1024 },
  fraud_investigation: { maxFiles: 20, label: 'Investigation Dossier', maxSizePerFile: 25 * 1024 * 1024 },
  investigation: { maxFiles: 20, label: 'Investigation Dossier', maxSizePerFile: 25 * 1024 * 1024 },
  starter: { maxFiles: 10, label: 'Professional Fraud Assessment', maxSizePerFile: 15 * 1024 * 1024 },
  dossier: { maxFiles: 20, label: 'Investigation Dossier', maxSizePerFile: 25 * 1024 * 1024 },
  corporate: { maxFiles: 30, label: 'Corporate Investigation', maxSizePerFile: 50 * 1024 * 1024 }
};

// Allowed file types
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.pdf'];

const ScamCheckSubmit = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tierParam = searchParams.get('tier') || 'basic';
  const sessionId = searchParams.get('session_id');
  
  const [tier, setTier] = useState(tierParam);
  const [verified, setVerified] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionId, setSubmissionId] = useState('');
  const [fileError, setFileError] = useState('');
  const [entitlementUsed, setEntitlementUsed] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    email: '',
    scam_type: '',
    website_or_phone: '',
    description: '',
    files: [] // Multiple files
  });

  // Get tier config
  const tierConfig = TIER_FILE_LIMITS[tier] || TIER_FILE_LIMITS.basic;

  // Verify payment session and check entitlement
  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
        // No session - redirect to payment page
        toast.error('Please complete payment first.');
        navigate('/scam-check');
        return;
      }

      try {
        // Check entitlement with backend
        const response = await fetch(`${API_URL}/api/scam-check/verify-entitlement?session_id=${sessionId}`);
        const data = await response.json();
        
        if (response.ok && data.valid) {
          setVerified(true);
          setTier(data.tier || tierParam);
          setEntitlementUsed(false);
        } else if (data.used) {
          // Entitlement already used
          setEntitlementUsed(true);
          setVerified(false);
        } else {
          toast.error('Invalid or expired session. Please make a new purchase.');
          navigate('/scam-check');
        }
      } catch (error) {
        console.error('Verification error:', error);
        // Allow access on verification error (graceful degradation)
        setVerified(true);
      } finally {
        setVerifying(false);
      }
    };

    verifyPayment();
  }, [sessionId, navigate, tierParam]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const validateFile = (file) => {
    const maxSize = tierConfig.maxSizePerFile;
    
    // Check file extension
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      return `Invalid file type: ${file.name}. Only JPG, PNG, PDF allowed.`;
    }
    
    // Check file size
    if (file.size > maxSize) {
      return `File too large: ${file.name}. Maximum size is ${maxSize / (1024 * 1024)}MB.`;
    }
    
    return null;
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFileError('');
    
    if (selectedFiles.length === 0) return;
    
    // Check total file count
    const totalFiles = form.files.length + selectedFiles.length;
    if (totalFiles > tierConfig.maxFiles) {
      const error = `Maximum ${tierConfig.maxFiles} files allowed for ${tierConfig.label}. You selected ${totalFiles} files.`;
      setFileError(error);
      toast.error(error);
      e.target.value = '';
      return;
    }
    
    // Validate each file
    const validFiles = [];
    for (const file of selectedFiles) {
      const error = validateFile(file);
      if (error) {
        setFileError(error);
        toast.error(error);
        e.target.value = '';
        return;
      }
      validFiles.push(file);
    }
    
    // Add files to state
    setForm(prev => ({ ...prev, files: [...prev.files, ...validFiles] }));
    e.target.value = ''; // Reset input to allow re-selecting same files
  };

  const removeFile = (index) => {
    setForm(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
    setFileError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.email || !form.scam_type || !form.description) {
      toast.error('Please fill in all required fields.');
      return;
    }

    if (!sessionId) {
      toast.error('Invalid session. Please make a payment first.');
      navigate('/scam-check');
      return;
    }

    setSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('email', form.email);
      formData.append('scam_type', form.scam_type);
      formData.append('website_or_phone', form.website_or_phone);
      formData.append('description', form.description);
      formData.append('tier', tier);
      formData.append('session_id', sessionId);
      
      // Append all files
      form.files.forEach((file, index) => {
        formData.append('files', file);
      });

      const response = await fetch(`${API_URL}/api/scam-check/submit`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmissionId(data.submission_id || '');
        setSubmitted(true);
      } else if (data.entitlement_used) {
        setEntitlementUsed(true);
        setVerified(false);
      } else {
        throw new Error(data.detail || 'Submission failed');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit. Please try again or contact support.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-black pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gold/30 border-t-gold rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Verifying your purchase...</p>
        </div>
      </div>
    );
  }

  // Entitlement already used
  if (entitlementUsed) {
    return (
      <div className="min-h-screen bg-black pt-24 pb-16 flex items-center justify-center">
        <div className="container mx-auto px-4">
          <Card className="max-w-lg mx-auto bg-[#111] border-orange-500/30">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
              <h1 className="text-xl font-bold text-white mb-4">Scam Check Already Used</h1>
              <p className="text-gray-400 mb-6">
                This Scam Check purchase has already been used. Each payment unlocks one submission only.
              </p>
              <p className="text-gray-500 text-sm mb-6">
                Please purchase a new Scam Check to submit another case.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate('/scam-check')} className="bg-gold hover:bg-gold/90 text-black font-bold">
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Purchase New Scam Check
                </Button>
                <Button onClick={() => navigate('/')} variant="outline" className="border-gold/50 text-gold">
                  Return to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Verification failed
  if (!verified) {
    return (
      <div className="min-h-screen bg-black pt-24 pb-16 flex items-center justify-center">
        <div className="container mx-auto px-4">
          <Card className="max-w-lg mx-auto bg-[#111] border-gold/20">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-12 h-12 text-gold mx-auto mb-4" />
              <h1 className="text-xl font-bold text-white mb-4">Payment Required</h1>
              <p className="text-gray-400 mb-6">
                Please complete payment to access the Scam Check submission form.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate('/scam-check')} className="bg-gold hover:bg-gold/90 text-black font-bold">
                  Go to Scam Check
                </Button>
                <Button onClick={() => navigate('/contact')} variant="outline" className="border-gold/50 text-gold">
                  Contact Support
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Submission Success Page
  if (submitted) {
    return (
      <div className="min-h-screen bg-black pt-24 pb-16">
        <Helmet>
          <title>Submission Received | ActionFraudOne</title>
        </Helmet>
        
        <div className="container mx-auto px-4">
          <Card className="max-w-2xl mx-auto bg-[#111] border-green-500/30">
            <CardContent className="p-8">
              {/* Success Header */}
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-500" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-4">Your Request Has Been Received</h1>
                <p className="text-gray-400">
                  Our investigators will review the material and respond within <strong className="text-white">24–48 hours</strong> with 
                  a Scam Risk Report sent to <strong className="text-gold">{form.email}</strong>.
                </p>
              </div>
              
              {/* Service Label */}
              <div className="p-4 bg-gold/10 border border-gold/20 rounded-lg mb-6 text-center">
                <p className="text-gold text-sm">
                  <strong>Service:</strong> {tierConfig.label}
                </p>
                {submissionId && (
                  <p className="text-gray-500 text-xs mt-1">Reference: {submissionId}</p>
                )}
                {form.files.length > 0 && (
                  <p className="text-gray-500 text-xs mt-1">{form.files.length} file(s) uploaded</p>
                )}
              </div>
              
              {/* What Happens Next */}
              <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-5 mb-8">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Inbox className="w-5 h-5 text-blue-500" />
                  What happens next?
                </h3>
                <ul className="text-gray-400 text-sm space-y-2">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Please monitor your inbox and spam/junk folder for our report</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Our team may contact you if further information is needed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>For urgent matters, contact us via WhatsApp or email</span>
                  </li>
                </ul>
              </div>
              
              {/* Action Buttons - NO "Submit Another Case" option */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate('/')} className="bg-gold hover:bg-gold/90 text-black font-bold">
                  <Home className="w-4 h-4 mr-2" />
                  Return to Homepage
                </Button>
                <Button 
                  onClick={() => navigate('/scam-check')} 
                  variant="outline" 
                  className="border-gold/50 text-gold"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Purchase Another Scam Check
                </Button>
                <a 
                  href="https://wa.me/447386668966?text=Hi, I recently submitted a Scam Check request and have a question."
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="border-green-500/50 text-green-500 hover:bg-green-500/10 w-full sm:w-auto">
                    <WhatsAppIcon size={16} />
                    <span className="ml-2">Contact on WhatsApp</span>
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main Form
  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <Helmet>
        <title>Submit Scam Check | ActionFraudOne</title>
      </Helmet>

      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full mb-4">
              <Check className="w-4 h-4 text-green-500" />
              <span className="text-green-500 text-sm font-medium">Payment Confirmed</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Submit Your {tierConfig.label}
            </h1>
            <p className="text-gray-400">
              Please provide details about the suspicious content you'd like us to investigate.
            </p>
          </div>

          {/* Submission Notice */}
          <Card className="bg-blue-500/5 border-blue-500/20 mb-6">
            <CardContent className="p-5">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-blue-500" />
                Submission Notice
              </h3>
              <div className="text-gray-400 text-sm space-y-2">
                <p>
                  By submitting information to ActionFraudOne, you confirm that the material 
                  you provide is lawful and that you have the right to share it.
                </p>
                <p className="text-orange-400">
                  <strong>Important:</strong> This purchase unlocks one submission only. Please include all relevant information and evidence.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Form */}
          <Card className="bg-[#111] border-gold/20">
            <CardContent className="p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <Label className="text-white">Full Name *</Label>
                  <Input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    className="bg-black border-gold/20 text-white mt-1"
                    required
                    data-testid="scam-check-name-input"
                  />
                </div>

                {/* Email */}
                <div>
                  <Label className="text-white">Email Address *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className="bg-black border-gold/20 text-white mt-1"
                    required
                    data-testid="scam-check-email-input"
                  />
                  <p className="text-gray-500 text-xs mt-1">We'll send your Scam Risk Report to this email.</p>
                </div>

                {/* Scam Type */}
                <div>
                  <Label className="text-white">Type of Suspected Scam *</Label>
                  <select
                    name="scam_type"
                    value={form.scam_type}
                    onChange={handleChange}
                    className="w-full bg-black border border-gold/20 text-white rounded-md px-3 py-2 mt-1"
                    required
                    data-testid="scam-check-type-select"
                  >
                    <option value="">Select scam type</option>
                    {SCAM_TYPES.map((type, i) => (
                      <option key={i} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                {/* Website/Phone */}
                <div>
                  <Label className="text-white">Website Link or Phone Number Involved</Label>
                  <Input
                    name="website_or_phone"
                    value={form.website_or_phone}
                    onChange={handleChange}
                    placeholder="https://suspicious-site.com or +44 7XXX XXXXXX"
                    className="bg-black border-gold/20 text-white mt-1"
                    data-testid="scam-check-website-input"
                  />
                </div>

                {/* Description */}
                <div>
                  <Label className="text-white">Brief Description of the Situation *</Label>
                  <Textarea
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    placeholder="Describe what happened, include any relevant details about the suspicious communication..."
                    className="bg-black border-gold/20 text-white mt-1 min-h-[120px]"
                    required
                    data-testid="scam-check-description-input"
                  />
                </div>

                {/* Multi-File Upload */}
                <div>
                  <Label className="text-white">Upload Screenshots, Messages or Documents</Label>
                  <p className="text-gray-500 text-xs mt-1 mb-2">
                    {tier === 'basic' && 'Basic: up to 3 files'}
                    {tier === 'pro' && 'Pro: up to 5 files'}
                    {(tier === 'fraud_investigation' || tier === 'investigation') && 'Investigation Dossier: upload all relevant supporting evidence'}
                    {' • JPG, PNG, PDF only (max 10MB each)'}
                  </p>
                  
                  {/* File List */}
                  {form.files.length > 0 && (
                    <div className="mb-3 space-y-2">
                      {form.files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-green-500/5 border border-green-500/20 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-green-500/20 rounded flex items-center justify-center">
                              <FileText className="w-4 h-4 text-green-500" />
                            </div>
                            <div>
                              <p className="text-white text-sm font-medium truncate max-w-[200px]">{file.name}</p>
                              <p className="text-gray-500 text-xs">{(file.size / 1024).toFixed(1)} KB</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="p-1.5 hover:bg-red-500/10 rounded transition-colors"
                          >
                            <X className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      ))}
                      <p className="text-gray-500 text-xs text-center">
                        {form.files.length} of {tierConfig.maxFiles} files selected
                      </p>
                    </div>
                  )}
                  
                  {/* Upload Area - only show if under limit */}
                  {form.files.length < tierConfig.maxFiles && (
                    <div className={`border-2 border-dashed rounded-lg p-6 text-center hover:border-gold/40 transition-colors ${fileError ? 'border-red-500/50' : 'border-gold/20'}`}>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={handleFileChange}
                        className="hidden"
                        id="file-upload"
                        multiple
                        data-testid="scam-check-file-input"
                      />
                      <label htmlFor="file-upload" className="cursor-pointer">
                        <Upload className="w-8 h-8 text-gold/50 mx-auto mb-2" />
                        <p className="text-gray-400 text-sm">Click to upload evidence files</p>
                        <p className="text-gray-500 text-xs mt-1">
                          {tierConfig.maxFiles - form.files.length} more file(s) can be added
                        </p>
                      </label>
                    </div>
                  )}
                  
                  {/* File Error */}
                  {fileError && (
                    <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {fileError}
                    </p>
                  )}
                </div>

                {/* Submit */}
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-gold hover:bg-gold/90 text-black font-bold py-6"
                  data-testid="scam-check-submit-btn"
                >
                  {submitting ? (
                    'Submitting...'
                  ) : (
                    <>
                      <Send className="mr-2" size={18} />
                      Submit for Analysis
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Note */}
          <div className="mt-6 flex items-start gap-3 p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
            <Shield className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-gray-400 text-sm">
              All submissions are handled confidentially. Your information will only be used to investigate 
              the suspected scam and provide your report.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScamCheckSubmit;
